import { useEffect, useState } from 'react';
import { getAdBudget, getAdBudgetByOps } from 'api/marketing';
import { IAdBudgetDisplay, IAdBudget, BudgetPeriod, BudgetStatus } from 'types/marketing';
import { convertBudgetToDisplay, isBudgetNearlyExhausted, isInHours } from 'utils/marketing';
import useAdStatus from 'hooks/marketing/useAdStatus';

export interface BudgetStatusDisplay {
    isCostAllBudget: boolean;
    isBudgetNearlyExhausted: boolean;
    isPaused: boolean;
    isPaymentFailed: boolean;
    disableBudgetChange: boolean;
}

export const MentalHealthBid = 40;
export const DefaultSpecialtyBid = 60;

const useBudgetInfo = (
    isOps?: boolean,
    providerId?: number,
): [IAdBudgetDisplay, BudgetStatusDisplay, boolean, () => void] => {
    const [loading, setLoading] = useState(false);
    const [budgetInfo, setBudgetInfo] = useState<IAdBudgetDisplay>({
        budgetCap: 300,
        budgetPeriod: BudgetPeriod.WEEKLY,
        generalBidAmount: 0,
        enableAdvancedSettings: false,
    });
    const [budgetStatus, setBudgetStatus] = useState<BudgetStatusDisplay>({
        isCostAllBudget: false,
        isBudgetNearlyExhausted: false,
        isPaused: false,
        isPaymentFailed: false,
        disableBudgetChange: false,
    });
    const [adStatus] = useAdStatus(isOps, providerId);

    const fetchData = async () => {
        if (isOps && !providerId) return;
        try {
            setLoading(true);
            const result = isOps ? await getAdBudgetByOps(providerId!) : await getAdBudget();
            if (!result.error && result?.data?.data) {
                const data: IAdBudget = result?.data?.data;
                setBudgetInfo(convertBudgetToDisplay(data));
                setBudgetStatus({
                    isCostAllBudget: data.status === BudgetStatus.BUDGET_EXHAUSTED,
                    isBudgetNearlyExhausted: isBudgetNearlyExhausted(data),
                    isPaused: data.status === BudgetStatus.PAUSED,
                    isPaymentFailed: data.status === BudgetStatus.PAYMENT_OVERDUE,
                    disableBudgetChange:
                        data.status === BudgetStatus.ACTIVE &&
                        data.updatedAt !== undefined &&
                        isInHours(data.updatedAt, 24), // 24 小时内不能修改预算
                });
            } else if (!result.error && !result.data.data && adStatus.joinedAt) {
                // group 里的 provider 被 pause 以后会把所有数据清理掉
                setBudgetStatus((prev) => ({
                    ...prev,
                    isPaused: true,
                }));
            }
            setLoading(false);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [isOps, providerId]);

    return [budgetInfo, budgetStatus, loading, fetchData];
};

export default useBudgetInfo;
